import { useEffect, useState } from 'react';
import type { ApiClient, Grunddatenfeld } from '../api';
import { IconOk } from '../Icons';
import {
  ConfirmDialog, ContentSection, ErrorState, LoadingState,
} from '../muster';

/**
 * Die Projektgrunddaten — die projektkonstanten Plankopffelder.
 *
 * Heute stehen sie an jedem Blatt. Wechselt das Brandschutzbüro, müssen alle Pläne
 * aller Modelle angefasst werden — in TGAA_001 allein 56, über neun Modelle einige
 * Hundert. Hier stehen sie einmal und werden von dort geschrieben.
 */
export function Grunddaten({
  api, projektId,
}: { api: ApiClient; projektId: string }) {
  const [felder, setFelder] = useState<Grunddatenfeld[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [freigebenFrage, setFreigebenFrage] = useState(false);
  const [offen, setOffen] = useState(false);

  /** Was der Anwender geändert hat, aber noch nicht gespeichert. */
  const [entwurf, setEntwurf] = useState<Record<string, string>>({});

  const laden = async () => {
    try {
      setFelder(await api.grunddaten(projektId));
      setEntwurf({});
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { laden(); }, [api, projektId]);

  const speichern = async () => {
    if (Object.keys(entwurf).length === 0) return;

    setLaeuft(true);
    setMeldung(null);
    try {
      const a = await api.grunddatenSpeichern(projektId, entwurf);
      setMeldung(a && 'message' in a ? a.message : 'Gespeichert.');
      await laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  if (fehler) {
    return (
      <ContentSection titel="Projektgrunddaten">
        <ErrorState
          meldung="Die Projektgrunddaten konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => void laden()}
        />
      </ContentSection>
    );
  }

  if (!felder) {
    return (
      <ContentSection titel="Projektgrunddaten">
        <LoadingState text="Projektgrunddaten werden geladen …" zeilen={8} spalten={3} />
      </ContentSection>
    );
  }

  const aktive = felder.filter((f) => f.istAktiv);
  const gefuellt = aktive.filter((f) => f.wert).length;
  const fixiert = aktive.filter((f) => f.istFixiert).length;
  const weichenAb = aktive.filter((f) => f.weichtAb).length;
  const nichtSchreibbar = aktive.filter((f) => !f.schreibbar).length;
  const offeneUebertragung = aktive.filter((f) => f.wert && !f.uebertragenAm).length;
  const geaendert = Object.keys(entwurf).length;

  const bereiche: { name: string; titel: string; text: string }[] = [
    { name: 'bauherr', titel: 'Bauherr',
      text: 'Anschrift und Erreichbarkeit.' },
    { name: 'beteiligte', titel: 'Beteiligte',
      text: 'Die Planungsbüros. Sie stehen im Plankopf jedes Blatts.' },
    { name: 'bearbeiter', titel: 'Bearbeiter',
      text: 'Die Rollennamen im Plankopf. Am einzelnen Plan überschreibbar.' },
    { name: 'projekt', titel: 'Projekt',
      text: 'Teilprojekt, Höhenbezug, Leistungsphase.' },
    { name: 'vorlage', titel: 'Aus der Vorlage',
      text: 'Felder eines anderen Büros, leer im Bestand. Vermutlich zu entfernen.' },
  ];

  return (
    <ContentSection
      titel="Projektgrunddaten"
      beschreibung={'Die projektkonstanten Plankopffelder. Sie werden hier einmal '
        + 'gepflegt und in die Modelle geschrieben — über „In Modell übertragen" bei '
        + 'den Planständen oder über das Mitarbeiter-Add-in.'}
      meta={`${gefuellt} von ${aktive.length} gefüllt`}
    >

      <div className="modellwerte">
        <div>
          <div className="feldname">gefüllt</div>
          <div style={{ fontSize: 19, fontWeight: 300 }}>
            {gefuellt}
            <span className="sehr-schwach" style={{ fontSize: 12 }}>
              {' '}von {aktive.length}
            </span>
          </div>
        </div>
        <div>
          <div className="feldname">nicht übertragen</div>
          <div style={{ fontSize: 19, fontWeight: 300 }}>{offeneUebertragung}</div>
        </div>
        <div>
          <div className="feldname">fixiert</div>
          <div style={{ fontSize: 19, fontWeight: 300 }}>
            {fixiert}
            <span className="sehr-schwach" style={{ fontSize: 12 }}>
              {' '}von {aktive.length}
            </span>
          </div>
          {weichenAb > 0 && (
            <div className="sehr-schwach" style={{ fontSize: 11,
                                                   color: 'var(--warnung)' }}>
              {weichenAb} weichen im Modell ab
            </div>
          )}
        </div>
        <div>
          <div className="feldname">nicht schreibbar</div>
          <div style={{ fontSize: 19, fontWeight: 300 }}>{nichtSchreibbar}</div>
        </div>
      </div>

      {/* Die Regel muss oben stehen. Bei neun Modellen ist sie der Kern: Ohne
          Fixierung entscheidet, welches Modell zuletzt ausgelesen wurde. */}
      <div className="hinweis" style={{ marginTop: 12 }}>
        <span className="hinweis-zeichen">i</span>
        <div style={{ fontSize: 12 }}>
          <strong>Fixiert</strong> heißt: Die Plattform gewinnt. Ein Auslesen vermerkt
          den Wert aus dem Modell, überschreibt ihn aber nicht — beim nächsten
          Übertragen wird das Modell angeglichen.
          <br />
          <strong>Bearbeitbar</strong> heißt: Das Modell gewinnt. So kommt der Bestand
          herein.
          <div className="sehr-schwach" style={{ fontSize: 11, marginTop: 4 }}>
            Ohne Fixierung entscheidet bei neun Modellen, welches zuletzt ausgelesen
            wurde. Ein Wert, den Sie hier von Hand setzen, wird deshalb automatisch
            fixiert.
          </div>
        </div>
      </div>

      {/* Ein Feld ohne Revit-Parameter kann gepflegt, aber nicht geschrieben werden.
          Das muss sichtbar sein, sonst wundert man sich, warum der Wert nicht im
          Modell erscheint. */}
      {nichtSchreibbar > 0 && (
        <div className="hinweis" style={{ marginTop: 12 }}>
          <span className="hinweis-zeichen">i</span>
          <div>
            <strong>{nichtSchreibbar} Feld(er) ohne Parameter im Modell.</strong>
            <div style={{ fontSize: 12, marginTop: 3 }}>
              Sie werden hier geführt, aber nicht geschrieben — in den
              Titelblockfamilien fehlt die Stelle dafür. Erkennbar am Vermerk
              „nicht schreibbar" am Feld.
            </div>
          </div>
        </div>
      )}

      {meldung && (
        <div className="hinweis" style={{ marginTop: 12 }}>
          <IconOk size={13} /> {meldung}
        </div>
      )}

      <Projektbild api={api} projektId={projektId} />

      {!offen ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button onClick={() => setOffen(true)}>Grunddaten bearbeiten</button>

          {fixiert < aktive.length && (
            <button
              disabled={laeuft}
              onClick={async () => {
                setLaeuft(true);
                try {
                  const a = await api.grunddatenFixieren(projektId, true);
                  setMeldung(a && 'message' in a ? a.message : null);
                  await laden();
                } finally { setLaeuft(false); }
              }}
              title="Alle Felder gegen Überschreiben durch ein Auslesen schützen"
            >
              Alle fixieren
            </button>
          )}

          {fixiert > 0 && (
            <button
              disabled={laeuft}
              onClick={() => setFreigebenFrage(true)}
            >
              Alle freigeben
            </button>
          )}
        </div>
      ) : (
        <>
          {bereiche.map((b) => {
            const eigene = felder.filter((f) => f.bereich === b.name);

            if (eigene.length === 0) return null;

            return (
              <div key={b.name} style={{ marginTop: 20 }}>
                <div className="feldname" style={{ fontSize: 13 }}>{b.titel}</div>
                <div className="sehr-schwach" style={{ fontSize: 11, marginBottom: 8 }}>
                  {b.text}
                </div>

                {eigene.map((f) => (
                  <Feldzeile
                    key={f.schluessel}
                    feld={f}
                    entwurf={entwurf[f.schluessel]}
                    aendern={(w) => setEntwurf({ ...entwurf, [f.schluessel]: w })}
                    fixieren={async (an) => {
                      await api.grunddatenFixieren(projektId, an, [f.schluessel]);
                      await laden();
                    }}
                    modellUebernehmen={async () => {
                      await api.grunddatenModellUebernehmen(projektId, f.schluessel);
                      await laden();
                    }}
                  />
                ))}
              </div>
            );
          })}

          <div style={{ display: 'flex', gap: 8, marginTop: 20, alignItems: 'center' }}>
            <button
              className={geaendert > 0 ? 'betont' : undefined}
              disabled={laeuft || geaendert === 0}
              onClick={speichern}
            >
              {laeuft ? 'wird gespeichert …' : `Speichern${geaendert > 0 ? ` (${geaendert})` : ''}`}
            </button>

            <button
              disabled={laeuft}
              onClick={() => { setEntwurf({}); setOffen(false); }}
            >
              {geaendert > 0 ? 'Verwerfen' : 'Schließen'}
            </button>

            {geaendert > 0 && (
              <span className="sehr-schwach" style={{ fontSize: 11 }}>
                {geaendert} Änderung(en) noch nicht gespeichert
              </span>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        offen={freigebenFrage}
        art="warnung"
        titel="Alle Felder freigeben?"
        text={'Das nächste Auslesen übernimmt dann die Werte aus dem Modell — gepflegte '
          + 'Werte können dabei überschrieben werden.'}
        bestaetigenText="Alle freigeben"
        laeuft={laeuft}
        beiBestaetigen={async () => {
          setFreigebenFrage(false);
          setLaeuft(true);
          try {
            const a = await api.grunddatenFixieren(projektId, false);
            setMeldung(a && 'message' in a ? a.message : null);
            await laden();
          } finally { setLaeuft(false); }
        }}
        beiAbbrechen={() => setFreigebenFrage(false)}
      />
    </ContentSection>
  );
}

// --- Eine Feldzeile ---------------------------------------------------------

function Feldzeile({
  feld: f, entwurf, aendern, fixieren, modellUebernehmen,
}: {
  feld: Grunddatenfeld;
  entwurf: string | undefined;
  aendern: (wert: string) => void;
  fixieren: (an: boolean) => Promise<void>;
  modellUebernehmen: () => Promise<void>;
}) {
  const [hinweisOffen, setHinweisOffen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);

  const wert = entwurf ?? f.wert ?? '';
  const veraendert = entwurf !== undefined && entwurf !== (f.wert ?? '');

  const tun = async (was: () => Promise<void>) => {
    setLaeuft(true);
    try { await was(); } finally { setLaeuft(false); }
  };

  return (
    <div
      className="modellfeld"
      style={{ marginBottom: 6, opacity: f.istAktiv ? 1 : 0.55 }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline',
                    flexWrap: 'wrap' }}>
        <div style={{ minWidth: 200 }}>
          <div style={{ fontSize: 13 }}>
            {f.bezeichnung}
            {veraendert && (
              <span className="marke neutral" style={{ marginLeft: 6 }}>geändert</span>
            )}
          </div>

          <div className="sehr-schwach" style={{ fontSize: 10 }}>
            {f.revitParameter
              ? <code>{f.revitParameter}</code>
              : <span style={{ color: 'var(--warnung)' }}>nicht schreibbar</span>}
            {f.uebertragenAm && ' · übertragen'}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 240 }}>
          {f.art === 'auswahl' && f.auswahl ? (
            /* Eine Auswahl statt Freitext. Bei der Leistungsphase wandert ein
               Tippfehler — „LP 3" statt „LP3" — über das Übertragen in alle Pläne
               und fällt erst beim Bauherrn auf. */
            <select
              value={wert}
              onChange={(e) => aendern(e.target.value)}
              disabled={!f.istAktiv}
              style={{ width: '100%', fontSize: 13 }}
            >
              <option value="">— nicht gesetzt —</option>
              {f.auswahl.split('|').map((x) => (
                <option key={x} value={x}>{x}</option>
              ))}
              {/* Ein Bestandswert, der nicht in der Liste steht, muss sichtbar
                  bleiben — sonst sähe das Feld leer aus, obwohl etwas drinsteht. */}
              {wert && !f.auswahl.split('|').includes(wert) && (
                <option value={wert}>{wert} (nicht in der Liste)</option>
              )}
            </select>
          ) : f.art === 'mehrzeilig' ? (
            <textarea
              value={wert}
              onChange={(e) => aendern(e.target.value)}
              rows={2}
              disabled={!f.istAktiv}
              style={{ width: '100%', fontSize: 13 }}
            />
          ) : (
            <input
              value={wert}
              onChange={(e) => aendern(e.target.value)}
              disabled={!f.istAktiv}
              type={f.art === 'email' ? 'email' : f.art === 'url' ? 'url' : 'text'}
              placeholder={f.art === 'url' ? 'https://…' : undefined}
              style={{ width: '100%', fontSize: 13 }}
            />
          )}
        </div>

        {/* Der Schalter. Fixiert schützt gegen das Auslesen — bei neun Modellen der
            Kern der Sache. */}
        <button
          disabled={laeuft || !f.istAktiv}
          onClick={() => tun(() => fixieren(!f.istFixiert))}
          className={f.istFixiert ? 'betont' : undefined}
          style={{ padding: '3px 10px', fontSize: 11, minWidth: 96 }}
          title={f.istFixiert
            ? 'Fixiert — ein Auslesen überschreibt diesen Wert nicht. Klicken macht '
              + 'ihn wieder aus dem Modell übernehmbar.'
            : 'Bearbeitbar — das nächste Auslesen übernimmt den Wert aus dem Modell. '
              + 'Klicken schützt ihn.'}
        >
          {f.istFixiert ? 'fixiert' : 'bearbeitbar'}
        </button>

        {f.hinweis && (
          <button
            onClick={() => setHinweisOffen(!hinweisOffen)}
            title="Hinweis zum Feld"
            style={{ padding: '2px 8px', fontSize: 11 }}
          >
            {hinweisOffen ? '−' : 'i'}
          </button>
        )}
      </div>

      {hinweisOffen && f.hinweis && (
        <div className="sehr-schwach" style={{ fontSize: 11, marginTop: 6 }}>
          {f.hinweis}
        </div>
      )}

      {/* Weicht das Modell ab, ist das bei einem fixierten Feld kein Fehler — es wird
          beim Übertragen angeglichen. Sichtbar sein muss es trotzdem, denn manchmal
          trägt das Modell doch das Richtige. */}
      {f.weichtAb && (
        <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center',
                      flexWrap: 'wrap', fontSize: 12 }}>
          <span className="sehr-schwach">
            Im Modell: <strong>{f.wertImModell || '(leer)'}</strong>
          </span>

          <button
            disabled={laeuft}
            onClick={() => tun(modellUebernehmen)}
            style={{ padding: '2px 8px', fontSize: 11 }}
            title="Diesen Wert übernehmen. Die Fixierung bleibt."
          >
            übernehmen
          </button>

          {f.istFixiert && (
            <span className="sehr-schwach" style={{ fontSize: 11 }}>
              wird beim nächsten Übertragen angeglichen
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Das Projektbild für das Dashboard.
 *
 * Es erscheint rechts oben auf dem Dashboard, das auf der Startansicht der Modelle
 * sitzt. Nur PNG oder JPEG — andere Formate kann die PDF-Erzeugung nicht einbinden.
 */
function Projektbild({
  api, projektId,
}: { api: ApiClient; projektId: string }) {
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [bildFrage, setBildFrage] = useState(false);

  const hochladen = async (datei: File) => {
    setLaeuft(true);
    setMeldung(null);
    try {
      const a = await api.projektbildHochladen(projektId, datei);
      setMeldung(a.message);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="modellfeld" style={{ marginTop: 14 }}>
      <div className="feldname">Projektbild für das Dashboard</div>

      <p className="schwach" style={{ fontSize: 12, marginTop: 4 }}>
        Erscheint rechts oben auf dem Dashboard, das auf der Startansicht der Modelle
        liegt. PNG oder JPEG, bis 30 MB. Ein Querformat wirkt dort am besten — der
        Platz ist 126 mm breit.
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                    flexWrap: 'wrap', marginTop: 8 }}>
        <input
          type="file"
          accept="image/png,image/jpeg"
          disabled={laeuft}
          onChange={(e) => {
            const datei = e.target.files?.[0];
            if (datei) hochladen(datei);
            e.target.value = '';
          }}
          style={{ fontSize: 13 }}
        />

        <button
          type="button"
          className="m-knopf"
          disabled={laeuft}
          onClick={() => setBildFrage(true)}
        >
          Entfernen
        </button>

        <ConfirmDialog
          offen={bildFrage}
          art="loeschen"
          titel="Projektbild entfernen?"
          text="Das Bild steht danach in keinem Plankopf mehr."
          bestaetigenText="Entfernen"
          laeuft={laeuft}
          beiBestaetigen={async () => {
            setBildFrage(false);
            setLaeuft(true);
            try {
              const a = await api.projektbildEntfernen(projektId);
              setMeldung(a && 'message' in a ? a.message : 'Entfernt.');
            } finally { setLaeuft(false); }
          }}
          beiAbbrechen={() => setBildFrage(false)}
        />
      </div>

      {meldung && (
        <div className="sehr-schwach" style={{ fontSize: 11, marginTop: 8 }}>
          {meldung}
        </div>
      )}
    </div>
  );
}
